import {
  type Dispatch,
  type SetStateAction,
  Suspense,
  useCallback,
  useMemo,
} from 'react';
import Box from '@mui/material/Box';
import { alpha } from '@mui/system';
import {
  DataGridPro,
  type GridSortModel,
  GridPaginationModel,
  type GridGroupingColDefOverride,
} from '@mui/x-data-grid-pro';
import { useTranslation } from 'react-i18next';

import { type TWidgetFilters, useWidgetContext } from 'components/Dashboard/Widget';
import { useTableWidgetConfig } from './useTableWidgetConfig';
import TableWidgetIconLegend from './TableWidgetIconLegend';
import WidgetLoadingIndicator from 'components/Dashboard/components/WidgetLoadingIndicator';

type TTableWidgetProps = {
  options?: any;
  onRowClick?: (params: any) => void;
  disableOnRowClick?: boolean;
  renderToolbar?: boolean;
  hideFooterSelectedRowCount?: boolean;
  isLiveUpdating?: boolean;
  setIsLiveUpdating?: Dispatch<SetStateAction<boolean>>;
  setShowLiveUpdatingStatus?: Dispatch<SetStateAction<boolean>>;
};

const initOptions = {
  rowCount: 0,
  currentPage: 1,
  pageSize: 10,
  sort: 'id',
  order: 'desc',
  hideFooter: true,
  tableType: 'filter',
  hasNoWrapField: false,
};

const TableWidget = ({
  options = initOptions,
  onRowClick,
  disableOnRowClick = false,
  renderToolbar = false,
  hideFooterSelectedRowCount = true,
}: TTableWidgetProps) => {
  const { t } = useTranslation();
  const {
    widget,
    config: displayConfig,
    data,
    setWidgetFilters,
    widgetFilters,
    isLoading,
    isFetching,
    pagination,
  } = useWidgetContext() || {};

  const {
    config,
    columnVisibilityModel,
    setColumnVisibilityModel,
    paginatedRows,
    currentPage,
    currentPageSize,
    totalRowCount,
    pageSizeOptions,
    hasPagination,
  } = useTableWidgetConfig({ widget, displayConfig, data, pagination, options });

  // Tree data configuration - must be before any conditional returns
  const isTreeData = config?.isTree ?? false;

  // Memoize the tree data path getter to maintain stable reference
  const getTreeDataPath = useCallback((row: any) => row.path || [], []);

  // Custom grouping column configuration for tree data
  const groupingColDef = useMemo<GridGroupingColDefOverride | undefined>(() => {
    if (!isTreeData || !config?.groupingField) return undefined;

    const groupingFieldName = config.groupingField.field;

    return {
      headerName: config.groupingField.label || 'Hierarchy',
      width: 250,
      flex: 2,
      hideDescendantCount: false,
      valueGetter: (value, row) => {
        // CMSADMIN-1033.
        // For child rows, return empty string to hide the nested value ("total")
        // Keeps the tree structure intact while hiding child identifiers
        if (row._isChildRow) {
          return '';
        }
        // For parent rows, return the actual value from the grouping field.
        return row[groupingFieldName];
      },
    };
  }, [isTreeData, config?.groupingField]);

  const isInitialLoad = isLoading && !data?.results?.length;

  if (isInitialLoad) {
    return <WidgetLoadingIndicator />;
  }

  // Handle pagination change
  const handlePaginationModelChange = (model: GridPaginationModel) => {
    if (hasPagination && pagination) {
      pagination.onChange(model.page + 1, model.pageSize);
    }
  };

  return (
    <Suspense fallback={<WidgetLoadingIndicator />}>
      <Box
        data-testid="dashboard-table"
        sx={{
          width: '100%',
        }}
      >
        <DataGridPro
          density="standard"
          getRowClassName={() => (options?.hasNoWrapField ? `hasnowrap` : ``)}
          rows={paginatedRows}
          rowCount={totalRowCount}
          columns={config.columns}
          // Remove pagination props when using tree data, MUI wont display all "parent" rows/cells.
          pagination={!isTreeData}
          paginationMode={hasPagination ? 'server' : 'client'}
          paginationModel={{
            page: currentPage,
            pageSize: currentPageSize,
          }}
          onPaginationModelChange={handlePaginationModelChange}
          pageSizeOptions={pageSizeOptions}
          disableColumnMenu={true}
          disableRowSelectionOnClick={disableOnRowClick}
          hideFooterSelectedRowCount={hideFooterSelectedRowCount}
          // Remove pagination props when using tree data, MUI wont display all "parent" rows/cells.
          hideFooter={isTreeData || (!hasPagination && options.hideFooter)}
          columnVisibilityModel={columnVisibilityModel}
          onColumnVisibilityModelChange={setColumnVisibilityModel}
          // Tree data props
          treeData={isTreeData}
          getTreeDataPath={isTreeData ? getTreeDataPath : undefined}
          defaultGroupingExpansionDepth={isTreeData ? -1 : undefined}
          groupingColDef={groupingColDef}
          localeText={{
            noRowsLabel: t('blox.dashboards.table.no_data', 'No Data'),
          }}
          slotProps={{
            pagination: {
              labelRowsPerPage: t(
                'blox.dashboards.table.rows_per_page',
                'Rows per page:',
              ),
              labelDisplayedRows: ({ from, to, count }: { from: number; to: number; count: number }) =>
                `${from}–${to} ${t('blox.dashboards.table.of', 'of')} ${count !== -1 ? count : `${t('blox.dashboards.table.more_than', 'more than')} ${to}`}`,
            },
          }}
          loading={isLoading || isFetching}
          onRowClick={onRowClick ?? undefined}
          showToolbar={renderToolbar}
          disableColumnSorting={false}
          // Do NOT use sortingOrder prop, it is controlled by the widgetFilters and setWidgetFilters state.
          // TODO: pagination and sorting should be unified in the future with widgetFilters and setWidgetFilters.
          sortingMode="server"
          onSortModelChange={(newSortModel: GridSortModel) => {
            const sortDir = newSortModel[0]?.sort || widgetFilters?.sortDir;
            const sort = newSortModel[0]?.field || widgetFilters?.sort;

            setWidgetFilters((prevFilters: TWidgetFilters) => ({
              ...prevFilters,
              sort,
              sortDir,
            }));
          }}
          sx={(theme) => ({
            width: '100%',
            height: 'auto',
            color:
              theme.palette.mode === 'light'
                ? theme.blox.colors.bluegray
                : theme.blox.colors.white,
            border: 0,
            '& .MuiDataGrid-columnSeparator': { display: 'none' },
            '& .MuiDataGrid-cell, .MuiDataGrid-columnHeaders': {
              borderColor:
                theme.palette.mode === 'light'
                  ? theme.blox.colors.bluegray
                  : theme.blox.colors.white,
            },
            '& .MuiDataGrid-columnHeaderTitle': {
              fontWeight: 600,
              whiteSpace: 'normal',
              overflow: 'visible',
              textOverflow: 'clip',
              lineHeight: 'normal',
              height: 'auto',
              width: 'auto',
              color:
                theme.palette.mode === 'light'
                  ? theme.blox.colors.bluegray
                  : theme.blox.colors.white,
            },
            '& .MuiDataGrid-footerContainer': {
              borderTop: 0,
            },
            '& .MuiDataGrid-row:hover': {
              cursor: onRowClick ? 'pointer' : 'default',
            },
            '& .MuiDataGrid-cell[aria-colindex="1"]': {
              color: onRowClick ? `${theme.blox.table.link}` : 'inherit',
              textDecoration: onRowClick ? 'underline' : 'none',
              textDecorationColor: onRowClick
                ? `${alpha(theme.blox.table.link, 0.4)}`
                : 'inherit',
            },
            '& .MuiDataGrid-cell:hover': {
              textDecorationColor: 'inherit',
            },
            '& .hasnowrap': {
              height: { xs: '80px !important', xl: '60px !important' },
              maxHeight: { xs: '80px !important', xl: '60px !important' },
              minHeight: { xs: '80px !important', xl: '60px !important' },
              padding: '10px 0',
              borderBottom: '1px solid ' + theme.blox.table.text,
            },
            '& .hasnowrap:last-child': {
              borderBottom: '0',
            },
            '& .hasnowrap:hover': {
              background: `${theme.blox.table.hover}`,
            },
            '& .hasnowrap:hover .dontwrapme:after': {
              background: `linear-gradient(90deg, transparent 0%, ${theme.blox.table.hover} 94%)`,
            },
            '& .hasnowrap .MuiDataGrid-cell': {
              height: { xs: '60px !important', xl: '40px !important' },
              maxHeight: { xs: '60px !important', xl: '40px !important' },
              minHeight: { xs: '60px !important', xl: '40px !important' },
              alignItems: 'baseline',
              borderBottom: '0',
            },
            '& .MuiDataGrid-virtualScrollerContent': {
              height: {
                xs: options.hasNoWrapField
                  ? `calc(80px * ${options.pageSize}) !important`
                  : ``,
                xl: options.hasNoWrapField
                  ? `calc(60px * ${options.pageSize}) !important`
                  : ``,
              },
            },
          })}
        />
      </Box>

      {!!config?.icons && (
        <TableWidgetIconLegend
          baseTestId={widget?.module?.id}
          title={widget?.title}
          iconsConfig={config?.icons}
          legendConfig={config?.legend}
        />
      )}
    </Suspense>
  );
};

export default TableWidget;
